import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  IconButton,
  LinearProgress,
  Alert,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel
} from '@mui/material';
import {
  Mic as MicIcon,
  Stop as StopIcon,
  CloudUpload as UploadIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Delete as DeleteIcon,
  VolumeUp as VolumeIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon
} from '@mui/icons-material';

// Audio Description Component
const AudioDescriptionField = ({ 
  formData, 
  onChange, 
  isLoading,
  onAudioProcessing // Callback when audio is being processed
}) => {
  const [recordingState, setRecordingState] = useState('idle'); // idle, recording, processing
  const [uploadState, setUploadState] = useState('idle'); // idle, uploading, processing
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [error, setError] = useState('');
  const [showMethodDialog, setShowMethodDialog] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState('');
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Detect supported audio format for recording
  const detectSupportedFormat = () => {
    const formats = [
      { mimeType: 'audio/webm;codecs=opus', extension: 'webm' },
      { mimeType: 'audio/webm', extension: 'webm' },
      { mimeType: 'audio/mp4', extension: 'mp4' },
      { mimeType: 'audio/mp3', extension: 'mp3' },
      { mimeType: 'audio/ogg;codecs=vorbis', extension: 'ogg' },
    ];

    for (const format of formats) {
      if (MediaRecorder.isTypeSupported(format.mimeType)) {
        return format;
      }
    }
    return { mimeType: 'audio/webm', extension: 'webm' }; // fallback
  };

  // Check if browser supports recording
  const canRecord = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia && window.MediaRecorder);

  const handleMethodSelection = () => {
    setShowMethodDialog(true);
  };

  const handleMethodConfirm = () => {
    setShowMethodDialog(false);
    if (selectedMethod === 'record') {
      startRecording();
    } else if (selectedMethod === 'upload') {
      fileInputRef.current?.click();
    }
    setSelectedMethod('');
  };

  const startRecording = async () => {
    try {
      setError('');
      setRecordingState('recording');
      setRecordingDuration(0);

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100,
          channelCount: 1
        }
      });

      streamRef.current = stream;
      audioChunksRef.current = [];

      const supportedFormat = detectSupportedFormat();
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: supportedFormat.mimeType,
        audioBitsPerSecond: 64000
      });

      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { 
          type: supportedFormat.mimeType 
        });
        handleAudioComplete(audioBlob, supportedFormat.extension, 'recorded');
      };

      mediaRecorder.start(100);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 0.1);
      }, 100);

    } catch (error) {
      console.error('Error starting recording:', error);
      setError('Failed to start recording. Please check microphone permissions.');
      setRecordingState('idle');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recordingState === 'recording') {
      mediaRecorderRef.current.stop();
      streamRef.current?.getTracks().forEach(track => track.stop());
      clearInterval(timerRef.current);
      setRecordingState('processing');
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    console.log('file', file)
    console.log('file.type', file.type)


    setError('');
    
    // Validate file type
    const allowedTypes = ['audio/webm', 'audio/mp4', 'audio/ogg', 'audio/wav', 'audio/mpeg', 'audio/mp3'];
    if (!allowedTypes.includes(file.type)) {
      setError('Unsupported file type. Please use WebM, MP4, OGG, WAV, or MP3 files.');
      return;
    }

    // Validate file size (max 50MB)
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('File too large. Maximum size is 50MB.');
      return;
    }

    setUploadState('processing');
    
    // Get file extension
    const extension = file.name.split('.').pop()?.toLowerCase() || 'audio';
    
    handleAudioComplete(file, extension, 'uploaded');
  };

  const handleAudioComplete = async (audioData, extension, source) => {
    try {
      // Calculate duration (approximate for uploaded files)
      let duration = 0;
      if (source === 'recorded') {
        duration = recordingDuration / 60; // Convert to minutes
      } else {
        // For uploaded files, we'll estimate or you could use Web Audio API to get actual duration
        duration = audioData.size / (64000 / 8) / 60; // Rough estimate based on bitrate
      }

      // Create audio object to store in form data
      const audioInfo = {
        file: audioData,
        duration: duration,
        type: extension,
        source: source, // 'recorded' or 'uploaded'
        size: audioData.size
      };

      // Update form data
      onChange({
        target: {
          name: 'audioDescription',
          value: audioInfo
        }
      });

      // Notify parent component
      if (onAudioProcessing) {
        onAudioProcessing(audioInfo);
      }

      setRecordingState('idle');
      setUploadState('idle');

    } catch (error) {
      console.error('Error processing audio:', error);
      setError('Failed to process audio. Please try again.');
      setRecordingState('idle');
      setUploadState('idle');
    }
  };

  const playAudio = () => {
    if (!formData.audioDescription?.file) return;

    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    } else {
      const audioUrl = URL.createObjectURL(formData.audioDescription.file);
      audioRef.current = new Audio(audioUrl);
      audioRef.current.play();
      setIsPlaying(true);

      audioRef.current.onended = () => {
        setIsPlaying(false);
        URL.revokeObjectURL(audioUrl);
      };
    }
  };

  const removeAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
    
    onChange({
      target: {
        name: 'audioDescription',
        value: null
      }
    });
    
    setRecordingDuration(0);
    setError('');
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes) => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <VolumeIcon color="primary" />
        Audio Description (Optional)
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Record or upload an audio description of your business. This can be automatically converted to text.
      </Typography>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* No Audio State */}
      {!formData.audioDescription && recordingState === 'idle' && uploadState === 'idle' && (
        <Card variant="outlined" sx={{ borderStyle: 'dashed', bgcolor: 'grey.50' }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <VolumeIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Add Audio Description
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Record with your microphone or upload an existing audio file
            </Typography>
            <Button
              variant="contained"
              onClick={handleMethodSelection}
              disabled={isLoading}
              size="large"
            >
              Add Audio Description
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Recording State */}
      {recordingState === 'recording' && (
        <Card variant="outlined" sx={{ bgcolor: 'error.light', borderColor: 'error.main' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  width: 12, 
                  height: 12, 
                  borderRadius: '50%', 
                  bgcolor: 'error.main',
                  animation: 'pulse 1.5s infinite'
                }} />
                <Typography variant="h6">Recording...</Typography>
              </Box>
              <Typography variant="h6" color="error.main">
                {formatDuration(recordingDuration)}
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="error"
              onClick={stopRecording}
              startIcon={<StopIcon />}
              fullWidth
            >
              Stop Recording
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Processing State */}
      {(recordingState === 'processing' || uploadState === 'processing') && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Processing Audio...
            </Typography>
            <LinearProgress sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Please wait while we process your audio description.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Audio Present State */}
      {formData.audioDescription && recordingState === 'idle' && uploadState === 'idle' && (
        <Card variant="outlined" sx={{ bgcolor: 'success.light', borderColor: 'success.main' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckIcon color="success" />
                <Typography variant="h6">Audio Description Added</Typography>
              </Box>
              <IconButton onClick={removeAudio} color="error" size="small">
                <DeleteIcon />
              </IconButton>
            </Box>

            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Chip 
                label={`${formatDuration(formData.audioDescription.duration * 60)}`}
                size="small" 
                color="primary" 
              />
              <Chip 
                label={formatFileSize(formData.audioDescription.size)}
                size="small" 
              />
              <Chip 
                label={formData.audioDescription.source === 'recorded' ? 'Recorded' : 'Uploaded'}
                size="small" 
                variant="outlined"
              />
              <Chip 
                label={formData.audioDescription.type.toUpperCase()}
                size="small" 
                variant="outlined"
              />
            </Stack>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={playAudio}
                startIcon={isPlaying ? <PauseIcon /> : <PlayIcon />}
                size="small"
              >
                {isPlaying ? 'Pause' : 'Play'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleMethodSelection}
                startIcon={formData.audioDescription.source === 'recorded' ? <UploadIcon /> : <MicIcon />}
                size="small"
              >
                {formData.audioDescription.source === 'recorded' ? 'Upload Instead' : 'Record Instead'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Method Selection Dialog */}
      <Dialog open={showMethodDialog} onClose={() => setShowMethodDialog(false)}>
        <DialogTitle>Choose Audio Method</DialogTitle>
        <DialogContent>
          <FormControl component="fieldset">
            <FormLabel component="legend">How would you like to add your audio description?</FormLabel>
            <RadioGroup
              value={selectedMethod}
              onChange={(e) => setSelectedMethod(e.target.value)}
              sx={{ mt: 2 }}
            >
              {canRecord && (
                <FormControlLabel
                  value="record"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">Record with Microphone</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Record directly using your device's microphone
                      </Typography>
                    </Box>
                  }
                />
              )}
              <FormControlLabel
                value="upload"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">Upload Audio File</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Upload a pre-recorded audio file (WebM, MP4, OGG, WAV, MP3)
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMethodDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleMethodConfirm} 
            variant="contained"
            disabled={!selectedMethod}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*"
        onChange={handleFileUpload}
        style={{ display: 'none' }}
      />

      {/* CSS Animation for recording pulse */}
      <style jsx>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
      `}</style>
    </Box>
  );
};

export default AudioDescriptionField;